<app-configuration-page
  title="SMS Configuration"
  description="Control lesson reminder timing and the message students receive."
  [breadcrumbs]="[{ label: 'SMS Configuration' }]">

  <div *ngIf="successMessage" class="status-banner success">
    <mat-icon>check_circle</mat-icon>
    <span>{{ successMessage }}</span>
  </div>

  <div *ngIf="errorMessage" class="status-banner error">
    <mat-icon>error_outline</mat-icon>
    <span>{{ errorMessage }}</span>
  </div>

  <div *ngIf="showProgressSpinner" class="d-flex align-items-center text-muted">
    <mat-spinner diameter="20" class="me-2"></mat-spinner> Loading SMS settings&hellip;
  </div>

  <form [formGroup]="smsConfigForm" *ngIf="!showProgressSpinner" class="sms-config-form" novalidate>
    <mat-card class="configuration-card">
      <mat-card-header>
        <div class="sms-card-header">
          <div class="header-text">
            <p class="eyebrow text-muted mb-1">Reminders</p>
            <mat-card-title>Lesson reminders</mat-card-title>
            <mat-card-subtitle>Pick reminder intervals and craft the text that goes out.</mat-card-subtitle>
          </div>
          <div class="header-action">
            <mat-slide-toggle color="primary" formControlName="enableLessonReminder">
              Enable
            </mat-slide-toggle>
          </div>
        </div>
      </mat-card-header>

      <mat-card-content>
        <div class="helper-text" *ngIf="lessonReminderEnabled">
          Select which reminders to send.
        </div>

        <div class="reminder-grid" *ngIf="lessonReminderEnabled">
          <div class="reminder-option">
            <div>
              <div class="label">24 hours before</div>
            </div>
            <mat-slide-toggle color="primary" formControlName="enable24HourReminder"></mat-slide-toggle>
          </div>
          <div class="reminder-option">
            <div>
              <div class="label">48 hours before</div>
            </div>
            <mat-slide-toggle color="primary" formControlName="enable48HourReminder"></mat-slide-toggle>
          </div>
          <div class="reminder-option">
            <div>
              <div class="label">72 hours before</div>
            </div>
            <mat-slide-toggle color="primary" formControlName="enable72HourReminder"></mat-slide-toggle>
          </div>
        </div>

        <div class="mt-3" *ngIf="lessonReminderEnabled">
          <div class="form-field-label">Reminder message</div>
          <mat-form-field class="full-width-field" appearance="outline">
            <mat-label>Lesson reminder SMS content</mat-label>
            <textarea matInput formControlName="lessonReminderSMSTemplate" rows="6"></textarea>
            <mat-hint align="start">Use placeholders: &#123;$studentName&#125;, &#123;$dateTime&#125;, &#123;$instructorName&#125;, &#123;$studioPhoneNumber&#125;</mat-hint>
            <mat-hint align="end">{{ lessonReminderTemplateLength }} characters</mat-hint>
          </mat-form-field>
        </div>
      </mat-card-content>
      <mat-card-actions align="end">
        <button mat-stroked-button type="button" (click)="resetLessonReminderTemplate()" [disabled]="showProgressSpinner">
          Reset
        </button>
        <button mat-raised-button color="primary" type="button" (click)="onSubmit()" [disabled]="!smsConfigForm.valid || showProgressSpinner">
          Save
        </button>
      </mat-card-actions>
    </mat-card>

    <mat-card class="configuration-card d-none">
      <mat-card-header>
        <mat-card-title>New Enrollment SMS</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <mat-form-field class="full-width-field" appearance="outline">
          <mat-label>New enrollment SMS content</mat-label>
          <textarea matInput formControlName="newEnrollmentTemplate"></textarea>
        </mat-form-field>
      </mat-card-content>
      <mat-card-actions align="end">
        <button (click)="onSubmit()" mat-raised-button color="primary" [disabled]="!smsConfigForm.valid || showProgressSpinner">Save</button>
      </mat-card-actions>
    </mat-card>

    <!-- Commented templates retained for future use
    <mat-card class="configuration-card">
      <mat-card-header>
        <mat-card-title>Lesson Confirmation SMS</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <mat-slide-toggle formControlName="enableLessonConfirmation">Enable Lesson Confirmation SMS</mat-slide-toggle>
        <div class="mt-3" *ngIf="smsConfigForm.get('enableLessonConfirmation').value">
          <mat-form-field class="full-width-field" appearance="outline">
            <mat-label>Lesson Confirmation SMS Content</mat-label>
            <textarea matInput formControlName="lessonConfirmationTemplate"></textarea>
          </mat-form-field>
        </div>
      </mat-card-content>
      <mat-card-actions align="end">
        <button (click)="onSubmit()" mat-raised-button color="accent" [disabled]="!smsConfigForm.valid || showProgressSpinner">Save</button>
      </mat-card-actions>
    </mat-card>

    <mat-card class="configuration-card">
      <mat-card-header>
        <mat-card-title>Lesson Rescheduled SMS</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <mat-slide-toggle formControlName="enableLessonRescheduled">Enable Lesson Rescheduled SMS</mat-slide-toggle>
        <div class="mt-3" *ngIf="smsConfigForm.get('enableLessonRescheduled').value">
          <mat-form-field class="full-width-field" appearance="outline">
            <mat-label>Lesson Rescheduled SMS Content</mat-label>
            <textarea matInput formControlName="lessonRescheduledTemplate"></textarea>
          </mat-form-field>
        </div>
      </mat-card-content>
      <mat-card-actions align="end">
        <button (click)="onSubmit()" mat-raised-button color="accent" [disabled]="!smsConfigForm.valid || showProgressSpinner">Save</button>
      </mat-card-actions>
    </mat-card>

    <mat-card class="configuration-card">
      <mat-card-header>
        <mat-card-title>Thank You for First Time Lessons SMS</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <mat-slide-toggle formControlName="enableFirstTimeLessonThankYou">Enable Thank You for First Time Lessons SMS</mat-slide-toggle>
        <div class="mt-3" *ngIf="smsConfigForm.get('enableFirstTimeLessonThankYou').value">
          <mat-form-field class="full-width-field" appearance="outline">
            <mat-label>First Time Lesson Thank You SMS Content</mat-label>
            <textarea matInput formControlName="firstTimeLessonThankYouTemplate"></textarea>
          </mat-form-field>
        </div>
      </mat-card-content>
      <mat-card-actions align="end">
        <button (click)="onSubmit()" mat-raised-button color="accent" [disabled]="!smsConfigForm.valid || showProgressSpinner">Save</button>
      </mat-card-actions>
    </mat-card>
    -->
  </form>

</app-configuration-page>
